import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ToastPopup from '../popups/ToastPopup';
import { apiCall } from '../main/ApiCall';
import "../css/others/template.css";
import "../css/others/Table.css";
import { CreateEmailTemplate, createToastMessage, ToastMessage } from '../interFaces/interfaces';
const CreateTemplate: React.FC = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const username = 'Admin';
  const extractPlaceholders = (...texts: string[]): string[] => {
    const placeholderPattern = /\{\{([^}]+)\}\}/g;
    const matches = texts.flatMap(text => text.match(placeholderPattern) || []);
    return [...new Set(matches)];
  };
  const [template, setTemplate] = useState<CreateEmailTemplate>({
    name: '',
    subject: 'Welcome to {{company_name}}!',
    body: `Dear {{your_first_name}},\n\nWelcome to {{your_company_name}}! We're excited to have you on board.\n\nBest regards,\n{{your_company_name}} Team`,
    clientId: clientId || '',
    type: '',
    placeholders: [],
    extraAttributes: {
      logoUrl: '',
      themeColor: '#1a73e8',
    }
  });
  const [loadedTemplateType, setLoadedTemplateType] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionPosition, setSuggestionPosition] = useState({ top: 0, left: 0 });
  const [activeField, setActiveField] = useState<'subject' | 'body' | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<{ id: string, name: string, type: string }[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setPlaceholders(extractPlaceholders(template.subject, template.body));
  }, [template.subject, template.body]);
  useEffect(() => {
    const fetchTemplates = async () => {
      if (clientId) {
        setIsLoadingTemplates(true);
        try {
          const templates = await apiCall('get', `/api/email-templates/default/types/${clientId}`);
          setAvailableTemplates(templates);
        } catch (error: any) {
          setMessage(createToastMessage('Failed to load templates', 'error', 3000));
          setAvailableTemplates([]);
        } finally {
          setIsLoadingTemplates(false);
        }
      }
    };
    fetchTemplates();
  }, [clientId]);
  const handleTemplateSelect = async (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setTemplate({
        name: '',
        subject: 'Welcome to {{company_name}}!',
        body: `Dear {{your_first_name}},\n\nWelcome to {{your_company_name}}! We're excited to have you on board.\n\nBest regards,\n{{your_company_name}} Team`,
        clientId: clientId || '',
        type: '',
        placeholders: [],
        extraAttributes: {
          logoUrl: '',
          themeColor: '#1a73e8',
        }
      });
      setLoadedTemplateType('');
      return;
    }
    try {
      setIsLoading(true);
      const data = await apiCall('get', `/api/email-templates/client/${clientId}/template/${templateId}`);
      setTemplate({ name: data.name || '', subject: data.subject || '', body: data.body ? data.body.replace(/\\n/g, '\n') : '', clientId: data.clientId || clientId || '', type: data.type || '', placeholders: data.placeholders || [], extraAttributes: data.extraAttributes || { logoUrl: '', themeColor: '#1a73e8' } });
      setLoadedTemplateType(data.type || '');
    } catch (error: any) {
      setMessage(createToastMessage('Failed to load template details', 'error', 3000));
    } finally {
      setIsLoading(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, selectionStart } = e.target;
    setTemplate(prev => ({
      ...prev, [name]: name === 'type' ? value.toUpperCase().replace(/\s+/g, '_') : value
    }));
    if ((name === 'subject' || name === 'body') && selectionStart !== null && value.slice(selectionStart - 2, selectionStart) === '{{') {
      const rect = e.target.getBoundingClientRect();
      setSuggestionPosition({ top: rect.top - 40, left: rect.left + 20 });
      setActiveField(name as 'subject' | 'body');
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  const insertPlaceholder = (placeholder: string) => {
    if (!activeField) return;
    const field = activeField === 'subject' ? subjectRef.current : bodyRef.current;
    if (!field) return;
    const start = field.selectionStart;
    const end = field.selectionEnd;
    if (start === null || end === null) return;
    const value = field.value;
    const beforeCursor = value.slice(0, start);
    const afterCursor = value.slice(end);
    const lastDoubleCurly = beforeCursor.lastIndexOf('{{');
    if (lastDoubleCurly === -1) return;
    const newValue = beforeCursor.slice(0, lastDoubleCurly) + placeholder + afterCursor;
    setTemplate(prev => ({ ...prev, [activeField]: newValue }));
    setTimeout(() => {
      const cursorPos = lastDoubleCurly + placeholder.length;
      field.focus();
      field.setSelectionRange(cursorPos, cursorPos);
    }, 0);
    setShowSuggestions(false);
  };
  const validateForm = (): boolean => {
    const requiredFields = [{ field: template.name.trim(), message: 'Template Name' }, { field: template.subject.trim(), message: 'Subject' }, { field: template.body.trim(), message: 'Body' }, { field: template.type.trim(), message: 'Type' }];
    const missingField = requiredFields.find(({ field }) => !field);
    if (missingField) {
      setMessage(createToastMessage(`Please fill in the ${missingField.message} field`, "warning", 3000));
      return false;
    }
    if (loadedTemplateType && template.type === loadedTemplateType) {
      setMessage(createToastMessage('Template type cannot be the same as the loaded template', "warning", 3000));
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setIsLoading(true);
      const finalTemplate = { ...template, body: template.body, placeholders: extractPlaceholders(template.subject, template.body) };
      await apiCall('post', `/api/email-templates/client/${clientId}?username=${username}`, finalTemplate);
      setMessage(createToastMessage('Template created successfully!', "success", 3000));
      setTimeout(() => navigate(-1), 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to create template';
      setMessage(createToastMessage(errorMessage, "error", 3000));
    } finally {
      setIsLoading(false);
    }
  };
  const handleBack = () => navigate(-1);
  const SuggestionBox = () => (
    <div className="suggestion-box" style={{ top: suggestionPosition.top, left: suggestionPosition.left }}>
      <ul className="suggestion-list">
        {placeholders.map((ph, idx) => (
          <li key={idx} onClick={() => insertPlaceholder(ph)}>{ph}</li>
        ))}
      </ul>
    </div>
  );
  return (
    <div className="template-page-container">
      <div className="template-page-header">
        <h2>Create New Email Template</h2>
      </div>
      <div className="template-page-form">
        <div className="template-page-field">
          <label>Select Example Templates</label>
          {isLoadingTemplates ? (
            <select disabled>
              <option>Loading templates...</option>
            </select>
          ) : (
            <div>
              <select value={selectedTemplateId} onChange={(e) => handleTemplateSelect(e.target.value)}>
                <option value="">Select a template to load</option>
                {availableTemplates.map((template, index) => (
                  <option key={index} value={template.id}>{template.name} ({template.type})</option>
                ))}
              </select>
              <div>
                <small className="small">Select a template to load or click "Create New Template" to start from scratch</small>
              </div>
            </div>
          )}
        </div>
      </div>
      <form id="templateForm" className="template-page-form" onSubmit={handleSubmit}>
        <div className="template-page-field">
          <label>Type *</label>
          <input name="type" value={template.type} onChange={handleInputChange} placeholder="e.g., USER_WELCOME" required />
          <small className="small">Use UPPER_CASE_WITH_UNDERSCORES format</small>
        </div>
        <div className="template-page-field">
          <label>Template Name *</label>
          <input name="name" value={template.name} onChange={handleInputChange} placeholder="e.g., User Welcome Email" required />
        </div>
        <div className="template-page-field">
          <label>Subject *</label>
          <input ref={subjectRef} name="subject" value={template.subject} onChange={handleInputChange} placeholder="e.g., Welcome to {{company_name}}!" required />
        </div>
        <div className="template-page-field">
          <label>Body *</label>
          <textarea ref={bodyRef} name="body" rows={10} value={template.body} onChange={handleInputChange} placeholder={`e.g., \n\nDear {{first_name}},\n\nWelcome to {{company_name}}! We're excited to have you on board.\n\nBest regards,\n{{company_name}} Team`} required />
          {placeholders.length > 0 && (
            <div className="template-page-placeholders">
              <strong>Placeholders:</strong> {placeholders.join(', ')}
            </div>
          )}
        </div>
        <div className="buttons">
          <button type="button" onClick={handleBack} className="cancelbutton" disabled={isLoading}>Back</button>
          <button type="submit" form="templateForm" disabled={isLoading} className="savebutton">{isLoading ? "Creating..." : "Create Template"}</button>
        </div>
      </form>
      {showSuggestions && <SuggestionBox />}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default CreateTemplate;